import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

// Super Admin / Country Admin / Venue Admin
import SuperAdminLayout from "./layouts/SuperAdminLayout";
import SuperAdminLogin from "./pages/SuperAdmin/SuperAdminLogin";
import SuperAdminDashboard from "./pages/SuperAdmin/Dashboard";
import VenueAdminDashboard from "./pages/VenueAdmin/Dashboard";
import BannersPage from "./pages/SuperAdmin/Banners";
import PromosPage from "./pages/SuperAdmin/Promos";
import PushNotificationsPage from "./pages/SuperAdmin/PushNotifications";
import GamesPage from "./pages/SuperAdmin/Games";
import LocalGamesPage from "./pages/SuperAdmin/LocalGames/LocalGamesPage";
import VenuesList from "./pages/SuperAdmin/Venues/VenueList";
import ContactEnquiriesPage from "./pages/SuperAdmin/Venues/ContactEnquiriesPage";
import VenueDetails from "./pages/SuperAdmin/Venues/VenueDetails";
import GroundAvailabilityPage from "./pages/SuperAdmin/Venues/GroundAvailabilityPage";
import SARegisteredUsers from "./pages/SuperAdmin/Users/SARegisteredUsers";
import CountryAdminsPage from "./pages/SuperAdmin/CountryAdmins/CountryAdminsPage";

import ReservationsList from "./pages/SuperAdmin/Reservations/ReservationsList";
import ReservationDetail from "./pages/SuperAdmin/Reservations/ReservationDetail";
import UpcomingReservations from "./pages/SuperAdmin/Reservations/UpcomingReservations";
import RecurringHoldForm from "./pages/SuperAdmin/Reservations/RecurringHoldForm";
import AdminBookingsList from "./pages/SuperAdmin/Reservations/AdminBookingsList";
import PaymentsList from "./features/payments/pages/PaymentsList";
import SettlementsSection from "./features/settlements/components/SettlementsSection";
import SettlementReviewPage from "./features/settlements/pages/SettlementReviewPage";
import SettlementHistoryPage from "./features/settlements/pages/SettlementHistoryPage";
import PaymentDetails from "./features/payments/pages/PaymentDetails";
import CommissionPage from "./features/commission/pages/CommissionPage";
import Profile from "./pages/Profile/Profile";

import VenueAdminLogin from "./pages/VenueAdmin/VenueAdminLogin";
import LoginForm from "./pages/Admin/LoginForm"; // public login-form stays
import PublicVenuePage from "./pages/PublicVenue/PublicVenuePage";
import {
  resolveVenueSubdomainFromHostname,
} from "./pages/PublicVenue/publicVenueHostname";
import CountryAdminLogin from "./pages/SuperAdmin/CountryAdmins/CountryAdminLogin";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// --- simple public layout just to host toasts ---
const PublicLayout = ({ children }) => (
  <div>{children}</div>
);

// ---- helpers ----
const getRole = () => {
  try {
    const ctx = JSON.parse(localStorage.getItem("va_ctx") || "{}");
    return ctx?.role; // "superadmin" | "country_admin" | "venue_admin"
  } catch {
    return undefined;
  }
};

// Decide correct landing path based on ctx.role
// superadmin      → /super-admin/dashboard
// country_admin   → /country-admin/dashboard
// venue_admin     → /venue-admin/dashboard
const getLandingPath = () => {
  const jwt = localStorage.getItem("sa_jwt");
  if (!jwt) return "/venue-admin/login?force=1";

  try {
    const ctx = JSON.parse(localStorage.getItem("va_ctx") || "{}");

    switch (ctx.role) {
      case "venue_admin":
        return "/venue-admin/dashboard";
      case "country_admin":
        return "/country-admin/dashboard";
      case "superadmin":
      default:
        return "/super-admin/dashboard";
    }
  } catch (_) {
    return "/super-admin/dashboard";
  }
};

// ✅ Auth + Role guard
// - If not logged in → redirect to correct login page by section
// - If logged in but role not allowed for that section → redirect to correct landing
const RequireRole = ({ allow, children }) => {
  const token = localStorage.getItem("sa_jwt");
  const { pathname } = useLocation();

  // Not logged in: send to correct login page
  if (!token) {
    if (pathname.startsWith("/venue-admin")) {
      return <Navigate to="/venue-admin/login?force=1" replace />;
    }
    if (pathname.startsWith("/country-admin")) {
      return <Navigate to="/country-admin/login?force=1" replace />;
    }
    return <Navigate to="/super-admin/login?force=1" replace />;
  }

  // Logged in: validate role
  const role = getRole();

  // If role missing/corrupt, safest is: clear session and go to default login
  if (!role) {
    localStorage.removeItem("sa_jwt");
    localStorage.removeItem("va_ctx");
    return <Navigate to="/venue-admin/login?force=1" replace />;
  }

  // Block cross-area URL switching
  if (!allow.includes(role)) {
    return <Navigate to={getLandingPath()} replace />;
  }

  return children;
};

const getCurrentVenueSubdomain = () =>
  resolveVenueSubdomainFromHostname(window.location.hostname);

const PublicVenueRoute = () => (
  <PublicLayout>
    <PublicVenuePage
      subdomainOverride={getCurrentVenueSubdomain()}
    />
  </PublicLayout>
);

const PublicVenueBookRoute = () =>
  getCurrentVenueSubdomain()
    ? <PublicVenueRoute />
    : <Navigate to={getLandingPath()} replace />;

const RootRoute = () => {
  if (getCurrentVenueSubdomain()) {
    return <PublicVenueRoute />;
  }

  return <Navigate to={getLandingPath()} replace />;
};

const FallbackRoute = () =>
  getCurrentVenueSubdomain()
    ? <Navigate to="/" replace />
    : <Navigate to={getLandingPath()} replace />;

const App = () => {
  return (
    <Router>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        style={{ zIndex: 2147483647 }}
      />

      <Routes>
        {/* ===== PUBLIC LOGIN ROUTES ===== */}
        <Route
          path="/super-admin/login"
          element={
            <PublicLayout>
              <SuperAdminLogin />
            </PublicLayout>
          }
        />
        <Route
          path="/country-admin/login"
          element={
            <PublicLayout>
              <CountryAdminLogin />
            </PublicLayout>
          }
        />
        <Route
          path="/venue-admin/login"
          element={
            <PublicLayout>
              <VenueAdminLogin />
            </PublicLayout>
          }
        />
        <Route
          path="/login-form"
          element={
            <PublicLayout>
              <LoginForm />
            </PublicLayout>
          }
        />
        <Route
          path="/venues/:slug"
          element={<PublicVenueRoute />}
        />

        {/* ===== SUPER ADMIN AREA ===== */}
        <Route
          path="/super-admin"
          element={
            <RequireRole allow={["superadmin"]}>
              <SuperAdminLayout />
            </RequireRole>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<SuperAdminDashboard />} />
          <Route path="banners" element={<BannersPage />} />
          <Route path="promo-codes" element={<PromosPage />} />
          <Route path="games" element={<GamesPage />} />
          <Route path="local-games" element={<LocalGamesPage />} />
          <Route
            path="push-notifications"
            element={<PushNotificationsPage />}
          />
          <Route path="venues" element={<VenuesList />} />
          <Route path="contact-enquiries" element={<ContactEnquiriesPage />} />
          <Route path="venues/:id" element={<VenueDetails />} />
          <Route
            path="grounds/:groundId/availability"
            element={<GroundAvailabilityPage />}
          />
          <Route path="users" element={<SARegisteredUsers />} />
          <Route path="country-admins" element={<CountryAdminsPage />} />
          <Route path="admin-bookings" element={<AdminBookingsList />} />
          <Route path="reservations" element={<ReservationsList />} />
          <Route path="block-slot" element={<GroundAvailabilityPage />} />

          <Route path="reservations/:id" element={<ReservationDetail />} />
          <Route path="settlements" element={<SettlementsSection />} />
          <Route path="settlements/review/:id" element={<SettlementReviewPage />} />
          <Route path="settlements/history" element={<SettlementHistoryPage />} />
          <Route path="payments" element={<PaymentsList />} />
          <Route path="payments/:id" element={<PaymentDetails />} />
          <Route path="commission" element={<CommissionPage />} />
          <Route path="profile" element={<Profile />} />
          <Route path="reservations/recurring" element={<RecurringHoldForm />} />
          <Route
            path="reservations/upcoming"
            element={<UpcomingReservations />}
          />
        </Route>

        {/* ===== COUNTRY ADMIN AREA (shares layout) ===== */}
        <Route
          path="/country-admin"
          element={
            <RequireRole allow={["country_admin", "superadmin"]}>
              <SuperAdminLayout />
            </RequireRole>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<SuperAdminDashboard />} />
          {/* same pages as super-admin; backend enforces role-specific rules */}
          <Route path="banners" element={<BannersPage />} />
          <Route path="promo-codes" element={<PromosPage />} />
          <Route path="games" element={<GamesPage />} />
          <Route path="local-games" element={<LocalGamesPage />} />
          <Route
            path="push-notifications"
            element={<PushNotificationsPage />}
          />
          <Route path="venues" element={<VenuesList />} />
          <Route path="venues/:id" element={<VenueDetails />} />
          <Route
            path="grounds/:groundId/availability"
            element={<GroundAvailabilityPage />}
          />
          <Route path="users" element={<SARegisteredUsers />} />
          {/* country-admins list will be hidden in sidebar for CA; route kept for SA */}
          <Route path="country-admins" element={<CountryAdminsPage />} />
          <Route path="admin-bookings" element={<AdminBookingsList />} />
          <Route path="reservations" element={<ReservationsList />} />
          <Route path="block-slot" element={<GroundAvailabilityPage />} />

          <Route path="reservations/:id" element={<ReservationDetail />} />
          <Route path="settlements" element={<SettlementsSection />} />
          <Route path="settlements/review/:id" element={<SettlementReviewPage />} />
          <Route path="settlements/history" element={<SettlementHistoryPage />} />
          <Route path="payments" element={<PaymentsList />} />
          <Route path="payments/:id" element={<PaymentDetails />} />
          <Route path="commission" element={<CommissionPage />} />
          <Route path="profile" element={<Profile />} />
          <Route path="reservations/recurring" element={<RecurringHoldForm />} />
          <Route
            path="reservations/upcoming"
            element={<UpcomingReservations />}
          />
        </Route>

        {/* ===== VENUE ADMIN AREA (shares layout) ===== */}
        <Route
          path="/venue-admin"
          element={
            <RequireRole allow={["venue_admin"]}>
              <SuperAdminLayout />
            </RequireRole>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<VenueAdminDashboard />} />
          <Route path="banners" element={<BannersPage />} />
          <Route path="games" element={<GamesPage />} />
          <Route path="venues" element={<VenuesList />} />
          <Route path="venues/:id" element={<VenueDetails />} />
          <Route
            path="grounds/:groundId/availability"
            element={<GroundAvailabilityPage />}
          />
          <Route path="users" element={<SARegisteredUsers />} />
          <Route path="admin-bookings" element={<AdminBookingsList />} />
          <Route path="reservations" element={<ReservationsList />} />
          <Route path="block-slot" element={<GroundAvailabilityPage />} />

          <Route path="reservations/:id" element={<ReservationDetail />} />
          <Route path="settlements" element={<SettlementsSection />} />
          <Route path="settlements/review/:id" element={<SettlementReviewPage />} />
          <Route path="payments" element={<PaymentsList />} />
          <Route path="payments/:id" element={<PaymentDetails />} />
          <Route path="commission" element={<CommissionPage />} />
          <Route path="profile" element={<Profile />} />
          <Route path="reservations/recurring" element={<RecurringHoldForm />} />
          <Route
            path="reservations/upcoming"
            element={<UpcomingReservations />}
          />
        </Route>

        {/* ===== ROOT + FALLBACK ===== */}
        <Route path="/book" element={<PublicVenueBookRoute />} />
        <Route path="/" element={<RootRoute />} />
        <Route path="*" element={<FallbackRoute />} />
      </Routes>
    </Router>
  );
};

export default App;
